// client/src/store/thunks/authThunks.js
import { createAsyncThunk } from '@reduxjs/toolkit';
import Gateway from '../../api/gateway';

// ============================================================
// ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
// ============================================================

const safeStorageSet = (key, value) => {
  if (typeof localStorage !== 'undefined' && value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn(`Failed to save ${key}:`, e);
    }
  }
};

const safeStorageRemove = (key) => {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`Failed to remove ${key}:`, e);
    }
  }
};

// ============================================================
// AUTHENTICATION THUNKS
// ============================================================

export const checkAuthStatus = createAsyncThunk(
  'auth/checkStatus',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔐 Checking auth status via ImCon /sessions/whoami...');

      const sessionToken =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem('kratos_session_token')
          : null;

      if (!sessionToken) {
        console.log('ℹ️ No saved session token');
        return rejectWithValue(null);
      }

      const response = await Gateway.http.imcon.get('/api/sessions/whoami', {
        headers: { 'X-Session-Token': sessionToken },
      });

      const session = response.data?.data || response.data;
      const user = session?.identity || session?.user;

      if (!user) {
        console.log('ℹ️ Session exists but no identity in response');
        return rejectWithValue(null);
      }

      console.log('✅ Active session found:', user?.traits?.email || user?.id);

      safeStorageSet('auth_user', JSON.stringify(user));
      if (session.id) safeStorageSet('auth_session', session.id);
      safeStorageSet('auth_time', Date.now().toString());
      safeStorageSet('auth_method', 'kratos_token');

      return {
        identity: user,
        session: session,
        token: sessionToken,
        authMethod: 'kratos_token',
      };
    } catch (error) {
      if (error.response?.status === 401) {
        console.log('ℹ️ Session token expired or invalid — clearing');
        safeStorageRemove('kratos_session_token');
        safeStorageRemove('auth_user');
        safeStorageRemove('auth_session');
        safeStorageRemove('auth_time');
        safeStorageRemove('auth_method');
        return rejectWithValue(null);
      }

      if (error.response?.status === 404) {
        console.log('ℹ️ No active session');
        safeStorageRemove('kratos_session_token');
        safeStorageRemove('auth_user');
        return rejectWithValue(null);
      }

      console.error('❌ Auth status check failed:', error.message || error);
      return rejectWithValue(error.message || 'Authentication check failed');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue, dispatch }) => {
    try {
      console.log('🔄 Starting login via Gateway (ImCon proxy)...');

      const response = await Gateway.api.auth.login({ email, password });

      const sessionToken = response?.session_token;
      const session = response?.session;
      const user = response?.identity || session?.identity;

      if (!sessionToken) {
        throw new Error('No session_token returned');
      }

      safeStorageSet('kratos_session_token', sessionToken);
      if (user) safeStorageSet('auth_user', JSON.stringify(user));
      safeStorageSet('auth_time', Date.now().toString());
      if (session?.id) safeStorageSet('auth_session', session.id);
      safeStorageSet('auth_method', 'kratos_token');

      try {
        await dispatch(fetchAccess()).unwrap();
      } catch (accessError) {
        console.warn('⚠️ Failed to fetch access after login:', accessError);
      }

      return {
        identity: user,
        session,
        token: sessionToken,
        isAuthenticated: true,
        authMethod: 'kratos_token',
      };
    } catch (error) {
      const errorMessage = error.message || 'Login failed';
      console.error('💥 Login failed:', errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue, dispatch }) => {
    try {
      console.log('📝 Starting registration via Gateway (ImCon proxy)...');

      const response = await Gateway.api.auth.register(userData);

      console.log('✅ Registration successful');

      if (userData.email && userData.password) {
        console.log('🔄 Auto-login after registration...');
        const loginResult = await dispatch(
          login({
            email: userData.email,
            password: userData.password,
          })
        ).unwrap();

        return loginResult;
      }

      return {
        identity: response?.identity || null,
        session: null,
        token: null,
        isAuthenticated: false,
        authMethod: 'none',
      };
    } catch (error) {
      console.error('❌ Registration failed:', error);
      return rejectWithValue(error.message || 'Registration failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🚪 Logging out...');

      try {
        await Gateway.api.auth.logout();
      } catch (e) {
        console.warn('Gateway logout failed, continuing:', e.message);
      }

      safeStorageRemove('kratos_session_token');
      safeStorageRemove('auth_user');
      safeStorageRemove('auth_time');
      safeStorageRemove('auth_session');
      safeStorageRemove('auth_method');
      safeStorageRemove('auth_token');
      safeStorageRemove('access_token');
      safeStorageRemove('refresh_token');
      safeStorageRemove('game_auth_token');
      safeStorageRemove('game_server_url');
      safeStorageRemove('selected_character_id');
      safeStorageRemove('character_data');
      safeStorageRemove('playerId');

      console.log('✅ Logout successful, session cleared');
      return true;
    } catch (error) {
      console.error('❌ Logout failed:', error);

      safeStorageRemove('kratos_session_token');
      safeStorageRemove('auth_user');
      safeStorageRemove('auth_time');
      safeStorageRemove('auth_session');
      safeStorageRemove('auth_method');

      return rejectWithValue(error.message || 'Logout failed');
    }
  }
);

export const diagnoseConnection = createAsyncThunk(
  'auth/diagnoseConnection',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔍 Running connection diagnostics...');

      const diagnostics = {
        timestamp: new Date().toISOString(),
        gateway: {
          url: Gateway.utils.getServiceUrls().imcon,
          status: 'unknown',
        },
        localStorage: {
          hasSessionToken: !!localStorage.getItem('kratos_session_token'),
          hasUser: !!localStorage.getItem('auth_user'),
          hasSession: !!localStorage.getItem('auth_session'),
          hasAuthMethod: !!localStorage.getItem('auth_method'),
          hasGameToken: !!localStorage.getItem('game_auth_token'),
        },
        authMethod: localStorage.getItem('auth_method'),
      };

      try {
        const health = await Gateway.http.imcon.get('/api/health');
        diagnostics.gateway.status = 'connected';
        diagnostics.gateway.details = health.data;
      } catch (e) {
        diagnostics.gateway.status = 'error';
        diagnostics.gateway.error = e.message;
      }

      diagnostics.recommendations = [];

      if (diagnostics.localStorage.hasSessionToken) {
        diagnostics.recommendations.push(
          '✅ Using token-based auth (X-Session-Token header)'
        );
      } else {
        diagnostics.recommendations.push(
          '⚠️ No session token found. Please login to obtain one.'
        );
      }

      console.log('📊 Diagnostics complete:', diagnostics);
      return diagnostics;
    } catch (error) {
      console.error('❌ Diagnostics failed:', error);
      return rejectWithValue(error.message || 'Diagnostics failed');
    }
  }
);

export const refreshSession = createAsyncThunk(
  'auth/refreshSession',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      console.log('🔄 Refreshing session...');

      const sessionToken = localStorage.getItem('kratos_session_token');

      if (!sessionToken) {
        console.warn('⚠️ No session token found, cannot refresh');
        return rejectWithValue('No session token');
      }

      const result = await dispatch(checkAuthStatus()).unwrap();

      console.log('✅ Session refreshed successfully');
      return result;
    } catch (error) {
      console.error('❌ Session refresh failed:', error);
      return rejectWithValue(error.message || 'Session refresh failed');
    }
  }
);

// ============================================================
// ACCESS CONTROL THUNKS
// ============================================================

export const fetchAccess = createAsyncThunk(
  'auth/fetchAccess',
  async (_, { rejectWithValue }) => {
    try {
      const sessionToken =
        typeof localStorage !== 'undefined'
          ? localStorage.getItem('kratos_session_token')
          : null;

      if (!sessionToken) {
        console.log('ℹ️ fetchAccess: no session token');
        return rejectWithValue('No session token');
      }

      console.log('🔐 Fetching access profile from /api/access/me...');

      const response = await Gateway.http.imcon.get('/api/access/me', {
        headers: { 'X-Session-Token': sessionToken },
      });

      const data = response?.data?.data || response?.data;

      if (!data || !data.system_role) {
        console.warn('⚠️ fetchAccess: invalid response', data);
        return rejectWithValue('Invalid access response');
      }

      console.log('✅ Access profile loaded:', {
        role: data.system_role,
        characters: data.characters?.length || 0,
        routes: data.resources?.routes?.length || 0,
      });

      return data;
    } catch (error) {
      const status = error.response?.status;
      const errorMessage =
        error.response?.data?.error || error.message || 'Failed to fetch access';

      if (status === 401) {
        console.log('ℹ️ fetchAccess: 401, session invalid');
        return rejectWithValue({ status: 401, message: 'Session invalid' });
      }

      if (status === 404) {
        console.error(
          '❌ fetchAccess: 404 — маршрут /api/access/me не зарегистрирован. ' +
            'Перезапустите imcon (systemctl restart imcon).'
        );
        return rejectWithValue({
          status: 404,
          message: 'Access endpoint not registered. Restart imcon.',
        });
      }

      console.error('❌ fetchAccess failed:', errorMessage);
      return rejectWithValue({ status, message: errorMessage });
    }
  }
);

/**
 * Обновляет профиль текущего пользователя (avatar_base64, display_name)
 * и перезагружает access-профиль.
 *
 * @param {object} changes — { avatar_base64?: string | null, display_name?: string }
 */
export const updateMyProfile = createAsyncThunk(
  'auth/updateMyProfile',
  async (changes, { rejectWithValue, dispatch }) => {
    try {
      console.log('🖼️ updateMyProfile: sending', {
        fields: Object.keys(changes || {}),
        avatar_size: changes?.avatar_base64
          ? `${Math.round(changes.avatar_base64.length / 1024)} KB`
          : null,
      });

      const res = await Gateway.api.accessAdmin.updateMe(changes);

      if (!res?.success || !res?.data) {
        return rejectWithValue({
          message: res?.error || 'Invalid response from server',
        });
      }

      // Синхронизируем Redux — повторно тянем access (в т.ч. characters, wallet)
      try {
        await dispatch(fetchAccess()).unwrap();
      } catch (refreshErr) {
        console.warn('⚠️ updateMyProfile: fetchAccess after update failed:', refreshErr);
      }

      return res.data;
    } catch (error) {
      const status = error.response?.status;
      const apiError = error.response?.data?.error || error.message;

      if (status === 413) {
        return rejectWithValue({ message: 'Аватар слишком большой (макс. 30 KB)' });
      }
      if (status === 400) {
        return rejectWithValue({ message: apiError || 'Некорректные данные' });
      }
      if (status === 401) {
        return rejectWithValue({ message: 'Сессия истекла. Войдите заново.' });
      }

      console.error('❌ updateMyProfile failed:', apiError);
      return rejectWithValue({ message: apiError || 'Не удалось обновить профиль' });
    }
  }
);

export const emancipateCharacter = createAsyncThunk(
  'auth/emancipateCharacter',
  async ({ characterId, reason = null }, { rejectWithValue, dispatch }) => {
    try {
      const sessionToken = localStorage.getItem('kratos_session_token');
      if (!sessionToken) {
        throw new Error('No session token');
      }

      console.log('🎗️ Emancipating character:', characterId);

      const response = await Gateway.http.imcon.post(
        '/api/access/emancipate',
        { character_id: characterId, reason },
        { headers: { 'X-Session-Token': sessionToken } }
      );

      console.log('✅ Character emancipated:', characterId);

      try {
        await dispatch(fetchAccess()).unwrap();
      } catch (e) {
        console.warn('⚠️ Failed to refresh access after emancipate:', e);
      }

      return response?.data?.data || response?.data;
    } catch (error) {
      const status = error.response?.status;
      const errorMessage =
        error.response?.data?.error || error.message || 'Failed to emancipate';

      console.error('❌ emancipateCharacter failed:', errorMessage);
      return rejectWithValue({ status, message: errorMessage });
    }
  }
);

export const registerCharacterInAccess = createAsyncThunk(
  'auth/registerCharacterInAccess',
  async ({ characterId, source = 'created' }, { rejectWithValue, dispatch }) => {
    try {
      const sessionToken = localStorage.getItem('kratos_session_token');
      if (!sessionToken) {
        throw new Error('No session token');
      }

      console.log('📝 Registering character in access:', characterId, source);

      const response = await Gateway.http.imcon.post(
        '/api/access/characters',
        { character_id: characterId, source },
        { headers: { 'X-Session-Token': sessionToken } }
      );

      console.log('✅ Character registered:', characterId);

      try {
        await dispatch(fetchAccess()).unwrap();
      } catch (e) {
        console.warn('⚠️ Failed to refresh access after register:', e);
      }

      return response?.data?.data || response?.data;
    } catch (error) {
      const status = error.response?.status;
      const errorMessage =
        error.response?.data?.error || error.message || 'Failed to register character';

      console.error('❌ registerCharacterInAccess failed:', errorMessage);
      return rejectWithValue({ status, message: errorMessage });
    }
  }
);

export const fetchCharacters = createAsyncThunk(
  'auth/fetchCharacters',
  async (_, { rejectWithValue }) => {
    try {
      const sessionToken = localStorage.getItem('kratos_session_token');
      if (!sessionToken) {
        return rejectWithValue('No session token');
      }

      const response = await Gateway.http.imcon.get('/api/access/characters', {
        headers: { 'X-Session-Token': sessionToken },
      });

      const data = response?.data?.data || response?.data;
      return data?.characters || [];
    } catch (error) {
      const status = error.response?.status;
      const errorMessage =
        error.response?.data?.error || error.message || 'Failed to fetch characters';
      return rejectWithValue({ status, message: errorMessage });
    }
  }
);
